import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Check } from 'lucide-react';
import './PublicShell.css';

/**
 * Shared two-panel layout for the unauthenticated / onboarding screens
 * (login, registration, terms). Left: brand panel with the logo, event
 * summary and progress (the logo is the event title). Right: the form card.
 */

const DATE_OPTS = { month: 'short', day: 'numeric' };

export const formatEventDates = (event) => {
    if (!event?.StartDate) return '';
    const tz = event.TimeZone || undefined;
    const start = new Date(event.StartDate);
    const end = event.EndDate ? new Date(event.EndDate) : null;
    const fmt = (d, opts) => d.toLocaleDateString('en-US', { ...opts, timeZone: tz });
    if (!end || Number.isNaN(end.getTime())) {
        return fmt(start, { ...DATE_OPTS, year: 'numeric' });
    }
    const sameMonth = fmt(start, { month: 'short', timeZone: tz }) === fmt(end, { month: 'short', timeZone: tz });
    if (sameMonth) {
        return `${fmt(start, DATE_OPTS)}–${end.toLocaleDateString('en-US', { day: 'numeric', timeZone: tz })}, ${fmt(end, { year: 'numeric' })}`;
    }
    return `${fmt(start, DATE_OPTS)} – ${fmt(end, { ...DATE_OPTS, year: 'numeric' })}`;
};

export const formatEventLocation = (event) => {
    if (!event) return '';
    const parts = [event.Location, [event.City, event.StateProvince].filter(Boolean).join(', ')].filter(Boolean);
    return parts.join(' · ');
};

export const EventSummary = ({ event }) => {
    if (!event) return null;
    const dates = formatEventDates(event);
    const location = formatEventLocation(event);
    return (
        <div className="pshell-event">
            {event.EventDescription && <p className="pshell-event-desc">{event.EventDescription}</p>}
            <ul className="pshell-event-meta">
                {dates && (
                    <li><Calendar size={15} aria-hidden="true" /><span>{dates}</span></li>
                )}
                {location && (
                    <li><MapPin size={15} aria-hidden="true" /><span>{location}</span></li>
                )}
            </ul>
        </div>
    );
};

/**
 * steps: [{ label, description? }]; current is 0-based. Steps before `current` are done.
 */
export const Stepper = ({ steps, current, title = "What you'll do" }) => (
    <nav className="pshell-steps" aria-label="Progress">
        {title && <div className="pshell-steps-title">{title}</div>}
        <ol>
            {steps.map((s, i) => {
                const state = i < current ? 'done' : i === current ? 'current' : 'todo';
                return (
                    <li key={s.label} className={`pshell-step ${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                        <span className="pshell-step-marker" aria-hidden="true">
                            {state === 'done' ? <Check size={14} strokeWidth={3} /> : i + 1}
                        </span>
                        <span className="pshell-step-text">
                            <span className="pshell-step-label">{s.label}</span>
                            {s.description && <span className="pshell-step-desc">{s.description}</span>}
                        </span>
                    </li>
                );
            })}
        </ol>
    </nav>
);

const PublicShell = ({
    event,
    logoSrc,
    steps,
    currentStep = 0,
    stepsTitle,
    aside,            // extra content under the stepper in the brand panel
    eyebrow,          // small label above the card title
    title,
    subtitle,
    children,
    footer,           // below the card (links etc.)
    wide = false,     // wider card for long forms
    className = '',
}) => {
    const logo = logoSrc || event?.EventImage || '/Funds4Charity.png';
    return (
        <div className={`pshell ${wide ? 'pshell-wide' : ''} ${className}`}>
            <aside className="pshell-brand">
                <div className="pshell-brand-inner">
                    <Link to="/login" className="pshell-logo-link" aria-label="Funds4Charity">
                        <img src={logo} alt="Funds4Charity" className="pshell-logo" />
                    </Link>
                    <div className="pshell-brand-body">
                        <EventSummary event={event} />
                        {steps && <Stepper steps={steps} current={currentStep} title={stepsTitle} />}
                        {aside}
                    </div>
                    <div className="pshell-brand-foot">
                        <span>Need help? <a href="mailto:service@funds4charity.org">service@funds4charity.org</a></span>
                    </div>
                </div>
            </aside>

            <main className="pshell-main">
                <div className="pshell-main-inner">
                    <div className="pshell-mobile-brand">
                        <img src={logo} alt="Funds4Charity" className="pshell-logo" />
                        {event?.Name && <span className="pshell-mobile-event">{event.Name}</span>}
                    </div>
                    {steps && (
                        <div className="pshell-mobile-steps" aria-hidden="true">
                            Step {Math.min(currentStep + 1, steps.length)} of {steps.length} · {steps[Math.min(currentStep, steps.length - 1)]?.label}
                        </div>
                    )}
                    <section className="pshell-card">
                        {(eyebrow || title || subtitle) && (
                            <header className="pshell-card-head">
                                {eyebrow && <div className="pshell-eyebrow">{eyebrow}</div>}
                                {title && <h1 className="pshell-title">{title}</h1>}
                                {subtitle && <p className="pshell-subtitle">{subtitle}</p>}
                            </header>
                        )}
                        {children}
                    </section>
                    {footer && <div className="pshell-footer">{footer}</div>}
                </div>
            </main>
        </div>
    );
};

export const PublicShellStatus = ({ children, error = false }) => (
    <div className={`pshell-status ${error ? 'error' : ''}`} role={error ? 'alert' : 'status'}>
        {children}
    </div>
);

export default PublicShell;
